/**
 * Editor Search Bar
 * 
 * Find and Replace functionality for the TipTap document editor.
 * Provides text search, navigation between matches, and optional replace.
 */

import { Editor } from '@tiptap/react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronUp, ChevronDown, Replace, CaseSensitive } from 'lucide-react';

export interface EditorSearchProps {
  editor: Editor;
  onClose: () => void;
}

export function EditorSearch({ editor, onClose }: EditorSearchProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [replaceTerm, setReplaceTerm] = useState('');
  const [showReplace, setShowReplace] = useState(false);
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [matchCount, setMatchCount] = useState(0);
  const [currentMatch, setCurrentMatch] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Focus search input on mount
  useEffect(() => {
    searchInputRef.current?.focus();
    // Select any existing search term for easy replacement
    searchInputRef.current?.select();
  }, []);
  
  // Update search term in editor
  const updateSearch = useCallback((term: string, isCaseSensitive: boolean) => {
    if (!editor) return;
    
    editor.commands.setSearchTerm(term);
    editor.commands.setCaseSensitive(isCaseSensitive);
    
    // Count matches by checking DOM for search-result class
    // The extension adds this class to all matches
    setTimeout(() => {
      const results = document.querySelectorAll('.search-result');
      setMatchCount(results.length);
      if (results.length > 0) {
        setCurrentMatch(1);
      } else {
        setCurrentMatch(0);
      }
    }, 50);
  }, [editor]);
  
  // Handle search input change
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    updateSearch(value, caseSensitive);
  };
  
  // Handle case sensitivity toggle
  const handleCaseSensitiveToggle = () => {
    const newValue = !caseSensitive;
    setCaseSensitive(newValue);
    updateSearch(searchTerm, newValue);
  };
  
  // Navigate to next match
  const goToNext = () => {
    if (!editor || matchCount === 0) return;
    editor.commands.goToNextSearchResult();
    setCurrentMatch(prev => prev >= matchCount ? 1 : prev + 1);
  };
  
  // Navigate to previous match
  const goToPrevious = () => {
    if (!editor || matchCount === 0) return;
    editor.commands.goToPreviousSearchResult();
    setCurrentMatch(prev => prev <= 1 ? matchCount : prev - 1);
  };
  
  // Replace current match
  const handleReplace = () => {
    if (!editor || matchCount === 0) return;
    editor.commands.setReplaceTerm(replaceTerm);
    editor.commands.replace();
    // Update match count after replace
    setTimeout(() => {
      const results = document.querySelectorAll('.search-result');
      setMatchCount(results.length);
      if (results.length === 0) {
        setCurrentMatch(0);
      } else if (currentMatch > results.length) {
        setCurrentMatch(results.length);
      }
    }, 50);
  };
  
  // Replace all matches
  const handleReplaceAll = () => {
    if (!editor || matchCount === 0) return;
    editor.commands.setReplaceTerm(replaceTerm);
    editor.commands.replaceAll();
    // Clear match count
    setTimeout(() => {
      setMatchCount(0);
      setCurrentMatch(0);
    }, 50);
  };
  
  // Handle keyboard shortcuts in search input
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) {
        goToPrevious();
      } else {
        goToNext();
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };
  
  return (
    <div className="sticky top-0 z-10 bg-card border-b border-border shadow-sm">
      {/* Search Row */}
      <div className="flex items-center gap-2 px-4 py-2">
        {/* Toggle Replace Button */}
        <button
          onClick={() => setShowReplace(!showReplace)}
          className={`p-1.5 rounded hover:bg-muted ${showReplace ? 'bg-muted text-primary' : 'text-muted-foreground'}`}
          title="Toggle Replace"
        >
          <Replace className="w-4 h-4" />
        </button>
        
        {/* Search Input */}
        <div className="flex-1 flex items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Find in document..."
              className="w-full px-3 py-1.5 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            {searchTerm && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                {matchCount > 0 ? `${currentMatch} of ${matchCount}` : 'No results'}
              </span>
            )}
          </div>
          
          {/* Case Sensitive Toggle */}
          <button
            onClick={handleCaseSensitiveToggle}
            className={`p-1.5 rounded hover:bg-muted ${caseSensitive ? 'bg-muted text-primary' : 'text-muted-foreground'}`}
            title="Case Sensitive"
          >
            <CaseSensitive className="w-4 h-4" />
          </button>
          
          {/* Navigation Buttons */}
          <button
            onClick={goToPrevious}
            disabled={matchCount === 0}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground disabled:opacity-50 disabled:cursor-not-allowed"
            title="Previous (Shift+Enter)"
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            onClick={goToNext}
            disabled={matchCount === 0}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground disabled:opacity-50 disabled:cursor-not-allowed"
            title="Next (Enter)"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>
        
        {/* Close Button */}
        <button
          onClick={onClose}
          className="p-1.5 rounded hover:bg-muted text-muted-foreground"
          title="Close (Escape)"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      
      {/* Replace Row (when expanded) */}
      {showReplace && (
        <div className="flex items-center gap-2 px-4 py-2 border-t border-border bg-muted/30">
          {/* Spacer for alignment with search row */}
          <div className="w-8" />
          
          {/* Replace Input */}
          <div className="flex-1 max-w-md">
            <input
              type="text"
              value={replaceTerm}
              onChange={(e) => setReplaceTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleReplace();
                } else if (e.key === 'Escape') {
                  onClose();
                }
              }}
              placeholder="Replace with..."
              className="w-full px-3 py-1.5 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          
          {/* Replace Buttons */}
          <button
            onClick={handleReplace}
            disabled={matchCount === 0}
            className="px-3 py-1.5 text-sm rounded-md border border-border bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Replace
          </button>
          <button
            onClick={handleReplaceAll}
            disabled={matchCount === 0}
            className="px-3 py-1.5 text-sm rounded-md border border-border bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Replace All
          </button>
        </div>
      )}
    </div>
  );
}
